// 리드 캡처 — 계산 결과 화면에서 이메일로 환급·안전 소식 받기(뉴스레터 double opt-in 재사용).
// 백엔드 /api/newsletter/subscribe(CSRF 예외·이중동의)로 안전하게 리드 확보. source 로 캠페인 귀속.
import { useState } from 'react'
import { FiMail, FiCheckCircle } from 'react-icons/fi'
import { api } from '../../lib/api'
import Button from '../../components/ui/Button'

const T = {
  ko: {
    title: '환급·안전 소식 이메일로 받기',
    desc: '산재보험료 환급 준비 팁과 소규모 사업장 안전 정보를 무료로 보내드려요.',
    placeholder: '이메일 주소',
    consent: '광고성 정보 수신에 동의합니다 (정보통신망법 §50)',
    btn: '무료로 받기',
    sending: '보내는 중…',
    done: '확인 메일을 보냈어요! 메일함에서 인증하면 구독이 완료됩니다.',
    err: '잠시 후 다시 시도해주세요.',
    invalid: '올바른 이메일을 입력해주세요.',
  },
  en: {
    title: 'Get refund & safety tips by email',
    desc: 'Free tips on preparing for the premium refund and small-workplace safety.',
    placeholder: 'Email address',
    consent: 'I agree to receive marketing information.',
    btn: 'Subscribe free',
    sending: 'Sending…',
    done: 'Check your inbox — confirm the email to finish subscribing.',
    err: 'Please try again later.',
    invalid: 'Please enter a valid email.',
  },
}

export default function LeadCaptureBox({ lang, source }: { lang: 'ko' | 'en'; source: string }) {
  const t = T[lang]
  const [email, setEmail] = useState('')
  const [consent, setConsent] = useState(true)
  const [state, setState] = useState<'idle' | 'sending' | 'done'>('idle')
  const [err, setErr] = useState('')

  async function submit() {
    setErr('')
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setErr(t.invalid); return }
    if (!consent) { setErr(t.consent); return }
    setState('sending')
    try {
      await api.post('/newsletter/subscribe', {
        email: email.trim(), locale: lang, marketing_consent: true, source,
      })
      setState('done')
    } catch {
      setErr(t.err); setState('idle')
    }
  }

  if (state === 'done') {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.06] px-4 py-3 text-sm text-emerald-300">
        <FiCheckCircle className="shrink-0" /> {t.done}
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-surface-elevated/40 bg-surface-card p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-ink">
        <FiMail className="text-blue-400" /> {t.title}
      </div>
      <p className="mt-1 text-[12px] text-ink-muted">{t.desc}</p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <input type="email" inputMode="email" value={email} placeholder={t.placeholder}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
          className="w-full rounded-lg border border-surface-elevated/60 bg-surface-elevated/30 px-3 py-2 text-sm text-ink focus:border-blue-400 focus:outline-none" />
        <Button onClick={submit} disabled={state === 'sending'} className="shrink-0">
          {state === 'sending' ? t.sending : t.btn}
        </Button>
      </div>
      <label className="mt-2 flex items-start gap-2 text-[11px] text-ink-faint">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
        {t.consent}
      </label>
      {err && <p className="mt-1 text-[12px] text-amber-300">{err}</p>}
    </div>
  )
}
